import { useCallback, useEffect, useRef } from "react";
// Operation 브랜치에는 히트맵 화면·훅이 없어 좌표 타입만 여기에 둔다 (빅데이터 분석 밀도 보기용)
interface HeatmapPoint {
  lat: number;
  lng: number;
}

/**
 * 카카오 지도 위에 히트맵을 그리는 캔버스 오버레이.
 *
 * 카카오 지도 SDK 에는 히트맵 기능이 없어서 직접 그린다. 방식은 일반적인 히트맵과 같다.
 *   1) 점마다 방사형 그라데이션을 흑백 캔버스에 겹쳐 찍어 밀도를 알파값으로 쌓는다
 *   2) 알파값을 색상표(파랑→초록→노랑→빨강)로 바꿔 칠한다
 *
 * 캔버스는 이 컴포넌트가 직접 그린다. 예전에는 map.getNode() 안에 끼워 넣었는데,
 * 지도 내부 DOM 을 건드리는 셈이라 relayout 과 겹치면 타일이 사라진다.
 * 지금은 부모(MapArea, position:relative)에 얹기만 하고 지도 DOM 은 손대지 않는다.
 *
 * 좌표 변환은 카카오면 projection, Leaflet(OSM)이면 latLngToContainerPoint 를 쓴다.
 *
 * 카카오는 확대·축소할 때 타일만 약 0.3초에 걸쳐 키우거나 줄이고, 그동안 이벤트를 주지 않는다.
 * 그사이 캔버스가 그대로 있으면 히트맵이 지도와 어긋나 보인다. 그래서 zoom_start 때
 * 캔버스에 타일과 같은 CSS 변환을 같은 속도로 걸고, 끝나면(idle) 새 위치로 다시 그린다.
 * Leaflet 은 UnifiedMap 에서 확대 애니메이션을 꺼 두어(zoomAnimation=false) 따로 할 일이 없다.
 */

interface Props {
  /** UnifiedMap 의 onCreate 로 받은 지도 인스턴스 */
  map: any;
  points: HeatmapPoint[];
  /** 점 하나의 반경(px) */
  radius?: number;
  /** 전체 불투명도 0~1 */
  opacity?: number;
  /** 색이 가장 진해지는 겹침 수. 작을수록 쉽게 빨개진다 */
  saturateAt?: number;
}

const PALETTE: Array<[number, string]> = [
  [0.0, "rgba(0,0,255,0)"],
  [0.25, "rgba(0,0,255,1)"],
  [0.45, "rgba(0,255,255,1)"],
  [0.65, "rgba(0,255,0,1)"],
  [0.85, "rgba(255,255,0,1)"],
  [1.0, "rgba(255,0,0,1)"],
];

// 카카오 확대·축소 애니메이션. 타일 크기를 프레임마다 재 보니(2026-10-08)
// zoom_start 다음 두 번째 프레임에 시작해 290ms 동안 1-(1-t)² 곡선으로 움직였고,
// 마우스 위치를 고정점으로 커졌다. 아래 cubic-bezier 는 그 곡선과 같은 모양이다.
const KAKAO_ZOOM_MS = 290;
const KAKAO_ZOOM_EASING = "cubic-bezier(0.333, 0.667, 0.667, 1)";
// idle 이 안 오는 경우(더 확대할 수 없는 레벨 등)에도 다시 그리도록 기다리는 상한
const ZOOM_END_FALLBACK_MS = 1000;

/** 알파값 → RGB 변환표 (256단계) */
function buildRamp(): Uint8ClampedArray {
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 256;
  const ctx = c.getContext("2d");
  if (!ctx) return new Uint8ClampedArray(1024);
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  PALETTE.forEach(([stop, color]) => g.addColorStop(stop, color));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1, 256);
  return ctx.getImageData(0, 0, 1, 256).data;
}

/** 점 하나를 찍을 흑백 브러시 */
function buildBrush(radius: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  const r = radius;
  c.width = c.height = r * 2;
  const ctx = c.getContext("2d");
  if (ctx) {
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    g.addColorStop(0, "rgba(0,0,0,1)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, r * 2, r * 2);
  }
  return c;
}

/**
 * 지도 종류에 맞는 좌표 변환 함수를 만든다.
 * 카카오면 projection, Leaflet 이면 latLngToContainerPoint 를 쓴다.
 */
function makeProjector(map: any): ((lat: number, lng: number) => any) | null {
  const leaflet = map?._leafletMap;
  if (leaflet && typeof leaflet.latLngToContainerPoint === "function") {
    return (lat, lng) => {
      try {
        return leaflet.latLngToContainerPoint([lat, lng]);
      } catch {
        return null;
      }
    };
  }
  const proj =
    typeof map?.getProjection === "function" ? map.getProjection() : null;
  if (proj && typeof proj.containerPointFromCoords === "function") {
    return (lat, lng) => {
      try {
        return proj.containerPointFromCoords(
          new window.kakao.maps.LatLng(lat, lng)
        );
      } catch {
        return null;
      }
    };
  }
  return null;
}

export default function HeatLayer({
  map,
  points,
  radius = 24,
  opacity = 0.75,
  saturateAt = 8,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rampRef = useRef<Uint8ClampedArray | null>(null);
  const brushRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  // 카카오 확대·축소 애니메이션 중인지. 구독 effect 가 다시 걸려도 이어지도록 ref 에 둔다.
  const zoomingRef = useRef(false);
  // 애니메이션 동안 캔버스에 건 변환(그린 시점 화면 → 새 화면). 다시 그리면 없앤다.
  const shiftRef = useRef<{ s: number; x: number; y: number } | null>(null);
  const drawRef = useRef<() => void>(() => {});

  if (!rampRef.current) rampRef.current = buildRamp();
  if (!brushRef.current) brushRef.current = buildBrush(radius);

  useEffect(() => {
    brushRef.current = buildBrush(radius);
  }, [radius]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !map) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    // 확대·축소 때 건 변환을 푼다. 같은 프레임에 새로 그리므로 화면이 튀지 않는다.
    if (shiftRef.current) {
      canvas.style.transition = "none";
      canvas.style.transform = "none";
      shiftRef.current = null;
    }

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    // 밀도 그림이라 1배로 충분하다. 고해상도로 그리면 픽셀 루프가 4배로 늘어난다.
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (points.length === 0) return;

    // 좌표 → 화면 위치 변환. 카카오와 Leaflet 둘 다 지원한다.
    // UnifiedMap 이 OSM 으로 빠지면 카카오 호환 객체에 _leafletMap 이 들어온다.
    const toPoint = makeProjector(map);
    if (!toPoint) return;

    // 1) 밀도를 알파로 쌓는다
    const brush = brushRef.current as HTMLCanvasElement;
    ctx.globalAlpha = 1 / saturateAt;
    let drawn = 0;
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i];
      const pt = toPoint(p.lat, p.lng);
      if (!pt) continue;
      // 화면 밖은 건너뛴다
      if (
        pt.x < -radius ||
        pt.y < -radius ||
        pt.x > w + radius ||
        pt.y > h + radius
      )
        continue;
      ctx.drawImage(brush, pt.x - radius, pt.y - radius);
      drawn += 1;
    }
    ctx.globalAlpha = 1;
    if (drawn === 0) {
      ctx.clearRect(0, 0, w, h);
      return;
    }

    // 2) 알파를 색으로 바꾼다
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = img.data;
    const ramp = rampRef.current as Uint8ClampedArray;
    for (let i = 3; i < data.length; i += 4) {
      const a = data[i];
      if (a === 0) continue;
      const o = a * 4;
      data[i - 3] = ramp[o];
      data[i - 2] = ramp[o + 1];
      data[i - 1] = ramp[o + 2];
      data[i] = Math.round(a * opacity);
    }
    ctx.putImageData(img, 0, 0);
  }, [map, points, radius, opacity, saturateAt]);

  const schedule = useCallback(() => {
    // 애니메이션 중에 다시 그리면 타일보다 먼저 최종 위치로 튄다. 끝날 때(idle)까지 미룬다.
    if (zoomingRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => drawRef.current());
  }, []);

  // 점·옵션이 바뀌면 다시 그린다
  useEffect(() => {
    drawRef.current = draw;
    schedule();
  }, [draw, schedule]);

  useEffect(() => {
    if (!map) return;

    const leaflet = (map as any)?._leafletMap;
    const kakao = window.kakao;
    const kakaoEvents = ["zoom_changed", "center_changed", "bounds_changed"];
    const leafletEvents = "move zoom moveend zoomend resize";
    const useLeaflet = Boolean(leaflet && typeof leaflet.on === "function");
    const useKakao =
      !useLeaflet && Boolean(kakao?.maps?.event?.addListener);

    let endTimer: number | undefined;
    // zoom_start 때 잡은 옛 화면 모서리 좌표. 변환을 걸기 전에 또 확대되면 처음 것을 쓴다.
    let pending: { a: any; b: any; w: number } | null = null;

    const onIdle = () => {
      window.clearTimeout(endTimer);
      zoomingRef.current = false;
      pending = null;
      schedule();
    };

    // 카카오는 zoom_start 직후 projection 이 이미 최종 상태로 바뀌어 있다(실측).
    // 지금 화면 두 모서리의 좌표를 잡아 두었다가 다음에 새 화면 어디로 갔는지 보면
    // 옛 화면 → 새 화면 변환(배율 s, 이동 x·y)이 나온다. 이걸 타일과 같은 속도로 건다.
    // 타일이 두 번째 프레임에 움직이기 시작하므로 변환도 두 번째 프레임에 건다.
    const onZoomStart = () => {
      const canvas = canvasRef.current;
      const proj =
        typeof map.getProjection === "function" ? map.getProjection() : null;
      if (!canvas || !proj) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      if (!pending) {
        pending = {
          a: proj.coordsFromContainerPoint(new kakao.maps.Point(0, 0)),
          b: proj.coordsFromContainerPoint(new kakao.maps.Point(w, h)),
          w,
        };
      }

      zoomingRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.clearTimeout(endTimer);
      endTimer = window.setTimeout(onIdle, ZOOM_END_FALLBACK_MS);

      const applyShift = () => {
        if (!zoomingRef.current || !pending) return;
        const { a, b, w } = pending;
        pending = null;
        const p = map.getProjection();
        const pa = p.containerPointFromCoords(a);
        const pb = p.containerPointFromCoords(b);
        const s = (pb.x - pa.x) / w;
        if (!Number.isFinite(s) || s <= 0) return;
        // 연달아 확대하면 앞서 건 변환 위에 이어 붙인다
        const prev = shiftRef.current ?? { s: 1, x: 0, y: 0 };
        const next = {
          s: prev.s * s,
          x: prev.x * s + pa.x,
          y: prev.y * s + pa.y,
        };
        shiftRef.current = next;
        canvas.style.transition = `transform ${KAKAO_ZOOM_MS}ms ${KAKAO_ZOOM_EASING}`;
        canvas.style.transform = `translate(${next.x}px, ${next.y}px) scale(${next.s})`;
      };
      requestAnimationFrame(() => requestAnimationFrame(applyShift));
    };

    if (useLeaflet) leaflet.on(leafletEvents, schedule);
    if (useKakao) {
      kakaoEvents.forEach((e) => kakao.maps.event.addListener(map, e, schedule));
      kakao.maps.event.addListener(map, "zoom_start", onZoomStart);
      kakao.maps.event.addListener(map, "idle", onIdle);
    }
    window.addEventListener("resize", schedule);
    schedule();

    return () => {
      if (useLeaflet) leaflet.off(leafletEvents, schedule);
      if (useKakao) {
        kakaoEvents.forEach((e) =>
          kakao.maps.event.removeListener(map, e, schedule)
        );
        kakao.maps.event.removeListener(map, "zoom_start", onZoomStart);
        kakao.maps.event.removeListener(map, "idle", onIdle);
      }
      window.removeEventListener("resize", schedule);
      window.clearTimeout(endTimer);
      zoomingRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [map, schedule]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 2,
        // 확대·축소 변환은 캔버스 왼쪽 위(지도 컨테이너 0,0)를 기준으로 계산한다
        transformOrigin: "0 0",
      }}
    />
  );
}
